<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success help-save" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
            <NevsButton class="error help-back" @click="backClick"><i class="fa-solid fa-arrow-left"></i> {{
                    $LANG.Get('buttons.back')
                }}
            </NevsButton>
        </div>
        <NevsCard class="form form-float">
            <NevsSelect v-model="receiptNote.supplier_id" :ajax="'select/customers'" :error="receiptNoteValidation.supplier_id"
                        :label="$LANG.Get('fields.supplier')"></NevsSelect>
            <div v-if="this.selectedSupplier !== null" class="supplier-details">
                {{ this.selectedSupplier.address }}<br/>
                {{ this.selectedSupplier.postal_code }} {{ this.selectedSupplier.city }}<br/>
                {{ this.selectedSupplier.country }}<br/>
            </div>
            <NevsTextField v-model="receiptNote.incoming_invoice_number" :label="$LANG.Get('fields.incomingInvoiceNumber')"></NevsTextField>
        </NevsCard>
        <NevsCard class="form form-float help-dates">
            <NevsDateField v-model="timeOfIssue.date" :error="timeOfIssueValidation.date"
                           :label="$LANG.Get('fields.dateOfIssue')"></NevsDateField>
            <NevsMaskedField v-model="timeOfIssue.time" :error="timeOfIssueValidation.time"
                             :label="$LANG.Get('fields.timeOfIssue')"
                             :mask="'NN:NN:NN'"></NevsMaskedField>
        </NevsCard>
        <NevsCard class="form form-float">
            <NevsSelect v-if="showCurrency" v-model="receiptNote.currency" :ajax="currencyEndpoint"
                        :error="receiptNoteValidation.currency"
                        :label="$LANG.Get('fields.currency')" :readonly="currencyReadonly"></NevsSelect>
            <NevsSelect v-if="(mode === 'add' || mode === 'copy') && sequences.length > 1"
                        v-model="receiptNote.receipt_note_sequence_id"
                        :error="receiptNoteValidation.receipt_note_sequence_id" :label="$LANG.Get('fields.sequence')"
                        :options="sequences"></NevsSelect>
            <NevsSelect v-model="receiptNote.locale"
                        :error="receiptNoteValidation.locale" :label="$LANG.Get('fields.language')"
                        :ajax="'select/languages'"></NevsSelect>
        </NevsCard>
        <NevsCard class="items-card">
            <NevsSelect v-model="selectedWare" :ajax="'select/wares'" :error="receiptNoteValidation.items"
                        :label="$LANG.Get('labels.selectItem')" class="item-select help-item"></NevsSelect>
            <div class="items-container">
                <table v-show="receiptNote.items.length !== 0" class="items-table">
                    <thead>
                    <tr class="items-table-header">
                        <td v-for="(header, key) in headers" :key="key" :style="{'width': header.width}">{{
                                header.text
                            }}
                        </td>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, key) in receiptNote.items" :key="key">
                        <td class="items-table-buttons">
                            <NevsButton @click="removeItemClick(key)"><i class="fa-solid fa-trash"></i></NevsButton>
                        </td>
                        <td>
                            <NevsTextField v-model="item.description" :readonly="true"></NevsTextField>
                        </td>
                        <td>
                            <NevsSelect v-model="item.measurement_unit_id" :readonly="true"
                                        :ajax="'select/measurement-units'"></NevsSelect>
                        </td>
                        <td>
                            <NevsNumberField v-model="item.quantity" :decimal-places="3"
                                             :thousand-separator="''"></NevsNumberField>
                        </td>
                        <td>
                            <NevsNumberField v-model="item.total"></NevsNumberField>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <div class="nevs-clear-float"></div>
            </div>
        </NevsCard>
    </div>
</template>

<script>
import ReceiptNote from '@/models/ReceiptNote.js';
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import moment from "moment";

export default {
    name: "EntityReceiptNote",
    components: {
        NevsNumberField, NevsTextField, NevsMaskedField, NevsDateField, NevsSelect, NevsButton, NevsCard
    },
    data() {
        return {
            loaded: false,
            mode: this.$route.params.mode,
            receiptNote: new ReceiptNote({
                id: this.$route.params.id
            }),
            receiptNoteValidation: new ReceiptNote({}, true),
            selectedSupplier: null,
            selectedWare: null,
            currencyReadonly: false,
            originalSupplierId: null,
            newSupplierData: null,
            supplierChanged: false,
            timeOfIssue: {
                date: null,
                time: null
            },
            timeOfIssueValidation: {
                date: '',
                time: ''
            },
            sequences: [],
            showCurrency: false,
            currencyEndpoint: 'select/currencies'
        }
    },
    computed: {
        headers() {
            let headers = [
                {
                    text: '',
                    sortable: false,
                    value: 'actions',
                    width: '50px',
                    class: 'pa-0'
                },
                {
                    text: this.$LANG.Get('fields.description'),
                    sortable: false,
                    value: 'description',
                    class: 'pa-0 description-width'
                },
                {
                    text: this.$LANG.Get('fields.measurementUnit'),
                    sortable: false,
                    value: 'measurement_unit',
                    class: 'pa-0',
                    width: "100px"
                },
                {
                    text: this.$LANG.Get('fields.quantity'),
                    sortable: false,
                    value: 'quantity',
                    class: 'pa-0',
                    width: "100px"
                },
                {
                    text: this.$LANG.Get('fields.total') + ' (' + this.receiptNote.currency + ')',
                    sortable: false,
                    value: 'total',
                    class: 'pa-0',
                    width: "100px"
                }
            ]
            return headers;
        }
    },
    watch: {
        'receiptNote.supplier_id': function () {
            if (this.receiptNote.supplier_id != null) {
                let vm = this;
                this.$API.APICall('get', 'customers/' + this.receiptNote.supplier_id, {}, (data, success) => {
                    if (success) {
                        vm.selectedSupplier = data.customer;
                    }
                });
            } else {
                this.selectedSupplier = null;
            }
        },
        'selectedWare': function () {
            if (this.selectedWare != null) {
                this.addItem();
            }
        },
        'receiptNote.currency': {
            handler: function () {
                this.loadCurrencyRate();
            },
            deep: true
        },
        'timeOfIssue': {
            handler: function () {
                this.showCurrency = false;
                this.currencyEndpoint = 'select/currencies';
                if (this.timeOfIssue.date + ' ' + this.timeOfIssue.time < this.$store.state.settings.EUR_TRANSITION) {
                    this.currencyEndpoint = 'select/currencies-hrk';
                }
                this.$nextTick(() => {
                    this.receiptNote.time_of_issue = this.timeOfIssue.date + ' ' + this.timeOfIssue.time;
                    this.showCurrency = true;
                });
            },
            deep: true
        },
        'receiptNote.time_of_issue': {
            handler: function () {
                this.timeOfIssue.date = this.receiptNote.time_of_issue.split(' ')[0];
                this.timeOfIssue.time = this.receiptNote.time_of_issue.split(' ')[1];
                if (this.receiptNote.time_of_issue > moment().format('YYYY-MM-DD') + ' 23:59:59') {
                    this.receiptNote.currency = this.$HELPERS.GetDefaultCurrency();
                    this.currencyReadonly = true;
                    this.loadCurrencyRate();
                } else {
                    this.currencyReadonly = false;
                    this.loadCurrencyRate();
                }
            },
            deep: true
        }
    },
    methods: {
        loadCurrencyRate() {
            let vm = this;
            this.$API.APICall('get', 'currencies/' + this.receiptNote.currency, {date: this.receiptNote.time_of_issue}, (data, success) => {
                if (success) {
                    if (data.rate != null) {
                        vm.receiptNote.currency_rate = data.rate;
                    }
                }
            }, false);
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        saveClick() {
            if (this.receiptNote.supplier_id !== this.originalSupplierId) {
                this.saveAction();
            } else {
                if (!this.supplierChanged) {
                    this.saveAction();
                } else {
                    let vm = this;
                    this.$LOCAL_BUS.TriggerEvent('popup', {
                        type: 'confirm',
                        text: this.$LANG.Get('alerts.supplierChangesQuestionReceiptNote'),
                        callback: (response) => {
                            if (response) {
                                vm.receiptNote.supplier_data = JSON.parse(JSON.stringify(vm.newSupplierData));
                                vm.supplierChanged = false;
                            }
                            vm.saveAction();
                        }
                    });
                }
            }
        },
        saveAction() {
            let success = true;

            if (this.receiptNote.supplier_id == null) {
                success = false;
                this.receiptNoteValidation.supplier_id = this.$LANG.Get('labels.supplierIdMissing');
            } else {
                this.receiptNoteValidation.supplier_id = '';
            }

            if ((this.receiptNote.time_of_issue == null) || (!this.$HELPERS.DateTimePattern().test(this.receiptNote.time_of_issue))) {
                success = false;
                this.timeOfIssueValidation.time = this.$LANG.Get('labels.timeOfIssueMissing');
            } else {
                this.timeOfIssueValidation.time = '';
            }

            if (this.receiptNote.receipt_note_sequence_id == null) {
                this.receiptNoteValidation.receipt_note_sequence_id = this.$LANG.Get('labels.sequenceMissing');
            } else {
                this.receiptNoteValidation.receipt_note_sequence_id = '';
            }

            let itemsValidation = '';
            if (this.receiptNote.items.length === 0) {
                success = false;
                itemsValidation = this.$LANG.Get('labels.noItems');
            } else {
                itemsValidation = '';
            }
            let termDescription = false;
            let termNegative = false;
            for (let item of this.receiptNote.items) {
                if (item.description === '') {
                    termDescription = true;
                }
                if (item.quantity<0 || item.total<0) {
                    termNegative = true;
                }
            }
            if (termDescription) {
                if (itemsValidation !== '') {
                    itemsValidation += '\n';
                }
                success = false;
                itemsValidation += this.$LANG.Get('labels.emptyItem');
            }
            if (termNegative) {
                if (itemsValidation !== '') {
                    itemsValidation += '\n';
                }
                success = false;
                itemsValidation += this.$LANG.Get('labels.negativeItem');
            }
            this.receiptNoteValidation.items = itemsValidation;

            if (success) {
                let data = JSON.parse(JSON.stringify(this.receiptNote));
                let vm = this;
                let action = 'post';
                let addition = '';
                if (this.mode === 'edit') {
                    action = 'put';
                    addition = '/' + this.receiptNote.id;
                }
                this.$API.APICall(action, 'receipt-notes' + addition, data, (data, success) => {
                    if (success) {
                        vm.$CROSS_TAB_BUS.TriggerEvent('reload-receipt-notes', {});
                        if (action === 'post') {
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                        } else {
                            vm.$LOCAL_BUS.TriggerEvent('notification', {text: this.$LANG.Get('alerts.recordSaved')});
                        }
                        if (vm.mode === 'add' || vm.mode === 'copy' || vm.mode === 'invoice') {
                            vm.backClick();
                        }
                    } else {
                        if (data.error === 'invalid sequence later') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.receiptNoteSequenceLater'),
                                type: 'alert'
                            });
                        } else if (data.error === 'invalid sequence earlier') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.receiptNoteSequenceEarlier'),
                                type: 'alert'
                            });
                        } else if (data.error === 'invalid year') {
                            vm.$LOCAL_BUS.TriggerEvent('popup', {
                                text: vm.$LANG.Get('alerts.receiptNoteInvalidYear'),
                                type: 'alert'
                            });
                        } else {
                            vm.$HELPERS.HandleServerError(vm, data);
                        }
                    }
                });
            }
        },
        addItem() {
            if (this.selectedWare === null) return;
            let vm = this;
            this.$API.APICall('get', 'wares/' + this.selectedWare, {}, (data, success) => {
                if (success) {
                    let newItem = {
                        description: (data.ware['name_' + vm.receiptNote.locale] !== undefined) ? data.ware['name_' + vm.receiptNote.locale] : data.ware.name,
                        quantity: 1,
                        measurement_unit_id: data.ware.measurement_unit_id,
                        total: 0,
                        ware_id: data.ware.id
                    }
                    vm.receiptNote.items.push(newItem);
                    vm.selectedWare = null;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            }, false);
        },
        removeItemClick(index) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.receiptNote.items.splice(index, 1);
                    }
                }
            });
        }
    },
    mounted() {
        this.$store.commit('setHelpOverlay', [
            {
                class: 'help-save',
                html: this.$LANG.Get('help.save'),
                left: -100,
                top: 0,
                width: 150
            },
            {
                class: 'help-back',
                html: this.$LANG.Get('help.back'),
                left: 85,
                top: -30,
                width: 300
            },
            {
                class: 'help-dates',
                html: this.$LANG.Get('help.dates'),
                left: 0,
                top: 0,
                width: 300
            },
            {
                class: 'help-item',
                html: this.$LANG.Get('help.item'),
                left: 300,
                top: -30,
                width: 300
            }
        ]);

        this.$store.commit('selectMenu', 'receiptNotes');
        this.$store.commit('selectSubMenu', null);

        this.sequences = this.$store.state.user.receipt_note_sequences;
        if (this.sequences.length > 0) {
            this.receiptNote.receipt_note_sequence_id = this.$store.state.user.default_receipt_note_sequence_id;
        } else {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
        }

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {
                    label: this.$LANG.Get('modules.receiptNotes'),
                    link: '/receipt-notes'
                },
                {
                    label: this.$LANG.Get('modules.newReceiptNote'),
                    link: null
                }
            ]);

            this.receiptNote.tax_system = this.$store.state.user.client.tax_system;
            this.receiptNote.time_of_issue = moment().format('YYYY-MM-DD HH:mm:ss');
            this.receiptNote.locale = this.$store.state.locale;

            this.$nextTick(() => {
                this.loaded = true;
            });
        } else if (this.mode === 'copy') {
            let vm = this;
            this.$API.APICall('get', 'receipt-notes/' + this.receiptNote.id, {}, (data, success) => {
                if (success) {
                    vm.receiptNote.id = 0;
                    vm.receiptNote.time_of_issue = moment().format('YYYY-MM-DD HH:mm:ss');
                    vm.receiptNote.supplier_id = data.receipt_note.supplier_id;
                    vm.receiptNote.currency = data.receipt_note.currency;
                    if (vm.receiptNote.time_of_issue >= this.$store.state.settings.EUR_TRANSITION && vm.receiptNote.currency === 'HRK') {
                        vm.receiptNote.currency = 'EUR';
                    }
                    let items = JSON.parse(JSON.stringify(data.receipt_note.items));
                    for (let i = 0; i < items.length; i++) {
                        items[i].id = undefined;
                    }
                    vm.receiptNote.items = items;
                    vm.receiptNote.locale = data.receipt_note.locale;
                    vm.receiptNote.cancels = 0;
                    vm.receiptNote.cancelled_by = 0;

                    this.$store.commit('setBreadcrumbs', [
                        {
                            label: this.$LANG.Get('modules.receiptNotes'),
                            link: '/receipt-notes'
                        },
                        {
                            label: this.$LANG.Get('modules.newReceiptNote'),
                            link: null
                        }
                    ]);
                    vm.$nextTick(() => {
                        vm.loaded = true;
                    });
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped>
.form {
    max-width: 300px;
    margin-bottom: 10px;
}

.form-float {
    width: 300px;
    float: left;
    margin-right: 10px;
    margin-bottom: 10px;
    padding: 15px;
}

.form-float h2 {
    margin: 0 0 5px;
}

.supplier-details {
    margin: 5px;
}

.items-card {
    clear: both;
}

.items-container {
    overflow-x: auto;
    white-space: nowrap;
}

.footer-container {
    margin-top: 10px;
    width: calc(100% - 20px);
}

.item-select {
    max-width: 500px;
}

.items-table {
    width: 100%;
    min-width: 1000px;
}

.items-table-header td {
    font-size: 12px;
    padding-left: 5px;
}

.items-table td {
    vertical-align: top;
}

.items-table-buttons {
    text-align: center;
}

.items-table-buttons span {
    cursor: pointer;
}

/* Match the button box to the field input (same 13px vertical padding) and offset it by the
   field wrapper's 5px top padding, so the buttons line up with the first row of inputs. */
.items-table-buttons .nevs-button {
    padding: 13px;
    margin-top: 5px;
}

</style>